import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Checkbox,
  Container,
  Group,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconWheat } from '@tabler/icons-react';
import NoNds from '@/components/common/NoNds';
import FormErrorsSummary from '@/components/common/FormErrorsSummary';
import SectionLabel from '@/components/common/SectionLabel';
import BasisPicker from '@/components/common/BasisPicker';
import UploadZone from '@/components/app/company/UploadZone';
import AgencyGateBanner from '@/components/app/sell/AgencyGateBanner';
import SellAttestationGate from '@/components/app/sell/SellAttestationGate';
import SellPriceOrientation from '@/components/app/sell/SellPriceOrientation';
import type { RecommendedSellPrice } from '@/types/bestPrice';
import AgentAssignmentBlock from '@/components/app/sell/form/AgentAssignmentBlock';
import FileList from '@/components/app/sell/form/FileList';
import LoadingConditionsFields from '@/components/app/sell/form/LoadingConditionsFields';
import {
  PRICE_MIN,
  WEIGHT_MIN,
  COMMENT_MAX,
  DECLARATION_NO_MAX,
  buildSaleDraft,
  candidateDuplicateKey,
  exchangeLotError,
  initialSellForm,
  isOfferComplete,
  loadingDateErrors,
  offerDuplicateKey,
  parentGoodsOf,
  signatureOf,
  stateFromOffer,
  type SellAddress,
  type SellFormState,
} from '@/components/app/sell/form/sellFormState';
import { sellService } from '@/api/services/SellService';
import { orderService } from '@/api/services/OrderService';
import { bestPriceService } from '@/api/services/BestPriceService';
import {
  SALE_TREE_QUERY_KEY,
  useAgencyAgreementMissed,
  useExchangeConstraints,
  useExchangeOrientation,
  useExchangeSession,
  useSaleOffer,
  useSaleTree,
} from '@/hooks/useSell';
import ExchangeOrientationCard from '@/components/app/sell/ExchangeOrientationCard';
import { formatMoney, formatPropertyCondition } from '@/lib/format';
import { todayIso } from '@/lib/date';
import { flattenSaleTree, groupSellByBasis } from '@/lib/sellTree';
import { getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import { useIsAgent } from '@/hooks/useRoles';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { ROUTES } from '@/routes/paths';

// Форма создания/редактирования предложения. Зеркалит структуру/стиль формы «Купить»
// (нумерованные секции, useBlocker, гейты, чек-лист), но поля и логика — «Продать»
// (одна культура + один лот, тяжёлая логистика, обязательное поручение агенту).
export default function SellOfferFormPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const editIdRaw = params.get('orderId');
  const editId = editIdRaw != null && !Number.isNaN(Number(editIdRaw)) ? Number(editIdRaw) : null;
  const editMode = editId != null;

  const agencyMissed = useAgencyAgreementMissed();
  const attestationRequired = useAttestationRequired();
  const isAgent = useIsAgent();

  const [state, setState] = useState<SellFormState>(initialSellForm);
  const [address, setAddress] = useState<SellAddress | null>(null);
  const [agentFileId, setAgentFileId] = useState<number | null>(null);
  const [agentSigned, setAgentSigned] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const dirtyRef = useRef(false);
  // Адрес, для которого уже запущен упреждающий прогрев расстояний — чтобы не дёргать прогрев повторно.
  const prewarmedAddressRef = useRef<string | null>(null);

  const update = useCallback((fn: (s: SellFormState) => SellFormState) => {
    dirtyRef.current = true;
    setState(fn);
  }, []);
  const set = useCallback(
    (patch: Partial<SellFormState>) => update((s) => ({ ...s, ...patch })),
    [update],
  );

  // ── Редактирование: префилл из существующего предложения ────────────────────
  // Форму рендерим ТОЛЬКО после префилла (edit), иначе блок поручения смонтируется с пустой
  // подписью и при заполнении полей ложно перейдёт в «данные изменились», сбросив подпись.
  const offerQuery = useSaleOffer(editId);
  const [prefillDone, setPrefillDone] = useState(!editMode);
  useEffect(() => {
    if (!editMode || prefillDone || !offerQuery.data) {
      return;
    }
    const offer = offerQuery.data;
    setState(stateFromOffer(offer));
    setAddress({ address: offer.address ?? '', title: offer.addressTitle, basisId: offer.basisId });
    if (offer.agentAssignmentFile?.id != null) {
      setAgentFileId(offer.agentAssignmentFile.id);
      setAgentSigned(true);
    }
    dirtyRef.current = false;
    setPrefillDone(true);
  }, [editMode, prefillDone, offerQuery.data]);

  // ── Справочники ─────────────────────────────────────────────────────────────
  const goodsClasses = useQuery({
    queryKey: ['goodsClasses'] as const,
    queryFn: ({ signal }) => orderService.getGoodsClasses(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationRequired,
    staleTime: 10 * 60_000,
  });
  const propertySets = useQuery({
    queryKey: ['goodsPropertySets', state.goodsType] as const,
    queryFn: ({ signal }) => orderService.getGoodsPropertySets(state.goodsType!, signal),
    enabled: isAuthenticated && !!state.goodsType,
    staleTime: 10 * 60_000,
  });
  const prepaymentTypes = useQuery({
    queryKey: ['sellPrepaymentTypes'] as const,
    queryFn: ({ signal }) => sellService.getPrepaymentTypes(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationRequired,
    staleTime: 10 * 60_000,
  });
  const shipmentModes = useQuery({
    queryKey: ['shipmentModes'] as const,
    queryFn: ({ signal }) => sellService.getShipmentModes(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationRequired,
    staleTime: 10 * 60_000,
  });
  // Биржа: ограничения (культура/качество/объём), торговое окно и ориентир цены (в правке).
  const { data: exchangeConstraints } = useExchangeConstraints();
  const { data: exchangeSession } = useExchangeSession();
  const { data: exchangeOrientation } = useExchangeOrientation(editId, editMode && state.toExchange);
  const allowedGoods = exchangeConstraints?.goodsTypes ?? [];
  const allowedSetId = exchangeConstraints?.propertiesSetIds?.[0];
  const lotError = exchangeLotError(state, exchangeConstraints ?? null);

  // При включении «биржевого» приводим к допустимым значениям: культура/качество (Пшеница 4 кл./12,5%) и
  // предоплата «без предоплаты» — их бэкенд и так требует. При выключении просто снимаем флаг.
  const onToExchangeToggle = useCallback(
    (checked: boolean) => {
      if (!checked) {
        set({ toExchange: false });
        return;
      }
      const patch: Partial<SellFormState> = { toExchange: true, prepayment: 'PERCENT_0' };
      // В правке культура/качество неизменны — не трогаем; в создании приводим к допустимым.
      if (!editMode && allowedGoods.length > 0 && (!state.goodsType || !allowedGoods.includes(state.goodsType))) {
        patch.goodsType = allowedGoods[0];
        patch.propsSetId = null;
      }
      set(patch);
    },
    [set, editMode, allowedGoods, state.goodsType],
  );

  // Рассинхрон биржевого выбора: культура или набор качества не входят в допустимые. В правке это не
  // исправить (культура/качество неизменны), поэтому и тумблер блокируем, и submit подстраховываем.
  const exchangeSelectionMismatch =
    state.toExchange &&
    exchangeConstraints != null &&
    ((!!state.goodsType && !allowedGoods.includes(state.goodsType)) ||
      (allowedSetId != null && state.propsSetId != null && state.propsSetId !== String(allowedSetId)));
  // В правке нельзя сделать биржевым оффер недопустимой культуры/качества (их в правке не изменить).
  const exchangeToggleDisabled =
    editMode &&
    exchangeConstraints != null &&
    ((!!state.goodsType && !allowedGoods.includes(state.goodsType)) ||
      (allowedSetId != null && state.propsSetId != null && state.propsSetId !== String(allowedSetId)));

  // Приведение культуры к допустимой в создании биржевого оффера — реактивно (в т.ч. если тумблер включили
  // до загрузки ограничений: тогда обработчик культуру не проставил, а этот эффект её выставит по приходу данных).
  useEffect(() => {
    if (!state.toExchange || editMode || allowedGoods.length === 0) {
      return;
    }
    if (!state.goodsType || !allowedGoods.includes(state.goodsType)) {
      set({ goodsType: allowedGoods[0], propsSetId: null });
    }
  }, [state.toExchange, state.goodsType, editMode, allowedGoods, set]);

  // Автовыбор допустимого набора качества (протеин 12,5%), как только он доступен для выбранной культуры.
  useEffect(() => {
    if (!state.toExchange || editMode || allowedSetId == null || !propertySets.data) {
      return;
    }
    const allowed = String(allowedSetId);
    if (propertySets.data.some((s) => String(s.propsSetId) === allowed) && state.propsSetId !== allowed) {
      set({ propsSetId: allowed });
    }
  }, [state.toExchange, state.propsSetId, editMode, allowedSetId, propertySets.data, set]);
  // Для упреждающей проверки дубликата нужна родительская культура (запрос моих предложений по basis+parent+year).
  const parentGoodsTypes = useQuery({
    queryKey: ['parentGoodsTypes'] as const,
    queryFn: ({ signal }) => orderService.getParentGoodsTypes(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationRequired,
    staleTime: 10 * 60_000,
  });
  // «Мои базисы» — все мои точки с существующими предложениями (из дерева продаж), без фильтра по
  // культуре: на знакомом базисе можно разместить и новую культуру. basisId берём из дерева.
  const saleTree = useSaleTree();
  const ownBases = useMemo(
    () =>
      groupSellByBasis(flattenSaleTree(saleTree.data)).map((base) => ({
        address: base.address,
        addressTitle: base.addressTitle,
        basisId: base.items[0]?.basisId,
      })),
    [saleTree.data],
  );

  const mainPropertyName = propertySets.data?.[0]?.properties?.[0]?.name;
  const propertyData = useMemo(
    () =>
      (propertySets.data ?? [])
        .filter((s) => s.propsSetId != null && s.properties?.[0]?.value != null)
        .map((s) => {
          const p = s.properties![0]!;
          return { value: String(s.propsSetId), label: formatPropertyCondition(p) };
        }),
    [propertySets.data],
  );

  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    return [cy - 1, cy, cy + 1].map((y) => String(y));
  }, []);

  // ── Файлы ────────────────────────────────────────────────────────────────────
  const onFileError = (e: unknown): void => {
    notifications.show({ color: 'red', message: getApiMessage(e) ?? t('auth.errors.generic') });
  };

  const uploadDeclaration = useMutation({
    mutationFn: (files: File[]) => sellService.uploadDeclaration(files),
    onSuccess: (uploaded) => update((s) => ({ ...s, declarationFiles: [...s.declarationFiles, ...uploaded] })),
    onError: onFileError,
  });
  const uploadSpecial = useMutation({
    mutationFn: (files: File[]) => sellService.uploadSpecialTerms(files),
    onSuccess: (uploaded) => update((s) => ({ ...s, specialTermsFiles: [...s.specialTermsFiles, ...uploaded] })),
    onError: onFileError,
  });
  const removeDeclaration = useMutation({
    mutationFn: (fileId: number) => sellService.deleteSaleFile(fileId),
    onSuccess: (_, fileId) => update((s) => ({ ...s, declarationFiles: s.declarationFiles.filter((f) => f.id !== fileId) })),
    onError: onFileError,
  });
  const removeSpecial = useMutation({
    mutationFn: (fileId: number) => sellService.deleteSaleFile(fileId),
    onSuccess: (_, fileId) => update((s) => ({ ...s, specialTermsFiles: s.specialTermsFiles.filter((f) => f.id !== fileId) })),
    onError: onFileError,
  });
  const downloadFile = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: onFileError,
  });

  // ── Производные ────────────────────────────────────────────────────────────
  const dateErrors = useMemo(() => loadingDateErrors(state), [state]);
  const complete = isOfferComplete(state, address);
  const draft = useMemo(() => buildSaleDraft(state, address), [state, address]);
  const signatureKey = useMemo(() => signatureOf(state, address), [state, address]);

  const missing = useMemo(() => {
    const list: string[] = [];
    if (!complete) {
      list.push('app.sell.form.check.fields');
    }
    if (state.declarationFiles.length === 0) {
      list.push('app.sell.form.check.declaration');
    }
    if (!agentSigned) {
      list.push('app.sell.form.check.agent');
    }
    if (!state.agreeSupply || !state.agreeOffer) {
      list.push('app.sell.form.check.agree');
    }
    return list;
  }, [complete, state.declarationFiles.length, state.agreeSupply, state.agreeOffer, agentSigned]);

  const canSubmit =
    complete && !lotError && !exchangeSelectionMismatch && agentSigned && agentFileId != null && state.agreeSupply && state.agreeOffer;

  // ── Сабмит ────────────────────────────────────────────────────────────────────
  const submit = useMutation({
    mutationFn: async (): Promise<number | null> => {
      const payload = { ...draft!, agentAssignmentFileId: agentFileId! };
      if (editMode) {
        await sellService.updateOffer(editId, { ...payload, version: offerQuery.data?.version ?? 0 });
        return editId;
      }
      // Создаём предложение сразу активным (минуя модерацию/BPMN) — оно появляется в списке
      // синхронно к ответу, без ожидания Camunda.
      return sellService.createOffer({ ...payload, activateImmediately: true });
    },
    onSuccess: () => {
      dirtyRef.current = false;
      // Дерево/группа ключуются по РОДИТЕЛЬСКОЙ культуре, а draft.goodsType — класс
      // («Пшеница 3 класс»). Ведём в список (он покажет базис→родительскую культуру),
      // чтобы не попасть на пустую группу из-за несовпадения класс↔родитель.
      queryClient.invalidateQueries({ queryKey: SALE_TREE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['saleOffers'] });
      notifications.show({ color: 'lime', message: t(editMode ? 'app.sell.form.updated' : 'app.sell.form.created') });
      // В список с предвыбором фильтра базис+культура (РОДИТЕЛЬСКАЯ — по ней группируется дерево).
      const parent = state.goodsType ? parentGoodsOf(state.goodsType, parentGoodsTypes.data ?? []) : undefined;
      const qs = new URLSearchParams();
      if (address?.address) {
        qs.set('address', address.address);
      }
      if (address?.title) {
        qs.set('addressTitle', address.title);
      }
      if (parent) {
        qs.set('goods', parent);
      }
      const q = qs.toString();
      navigate(q ? `${ROUTES.APP_SELL}?${q}` : ROUTES.APP_SELL);
    },
    onError: (error) => {
      const status = getStatus(error);
      if (status === 409) {
        // Бэкенд отклонил полный дубликат (тот же ключ: базис+культура+качество+год+объём+цена+предоплата+зачёт).
        setSubmitError(t('app.sell.form.duplicate'));
      } else if (status === 412) {
        // Оптимистичная блокировка (версия устарела) — order-service отдаёт 412.
        setSubmitError(t('app.sell.form.versionConflict'));
      } else if (status === 406) {
        setSubmitError(t('app.sell.form.errAddress'));
      } else {
        setSubmitError(isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')));
      }
    },
  });

  // Сабмит: упреждающая проверка дубликата (best-effort; бэкенд — авторитетная защита 409).
  // Сравниваем ключ кандидата с моими «живыми» предложениями по этому базису+культуре; при сбое
  // запроса не блокируем (бэкенд подстрахует). Адрес мог нормализоваться на бэке — тогда фронт может
  // не поймать, но серверная проверка вернёт 409.
  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null);
    const key = candidateDuplicateKey(state, address);
    if (key && address) {
      setChecking(true);
      try {
        const parent = parentGoodsOf(state.goodsType!, parentGoodsTypes.data ?? []);
        const existing = await sellService.getMySaleOffers({
          address: address.address,
          addressTitle: address.title ?? '',
          parentGoodsType: parent,
          year: state.cropYear,
        });
        if (existing.some((o) => o.id !== editId && offerDuplicateKey(o) === key)) {
          setSubmitError(t('app.sell.form.duplicate'));
          return;
        }
      } catch {
        // best-effort: сетевую ошибку проверки игнорируем, дубликат отсечёт бэкенд
      } finally {
        setChecking(false);
      }
    }
    submit.mutate();
  };

  // ── Защита от ухода с несохранёнными данными ───────────────────────────────
  useUnsavedChangesGuard(() => dirtyRef.current && !submit.isSuccess);

  // «Продать по ней» из ориентира цены: намеренно бросаем создание оффера и уходим на экран встречного
  // предложения этому покупателю (free-mode), соглашаясь на его предоплату. Лёгкий префилл — мой базис
  // (fromAddress) + объём (prefillWeightTon); культура/качество/CPT — со стороны прайса покупателя.
  const handleSellAtBest = useCallback(
    (row: RecommendedSellPrice): void => {
      if (!address?.address || row.purchaseOrderId == null) {
        return;
      }
      const parent = state.goodsType ? parentGoodsOf(state.goodsType, parentGoodsTypes.data ?? []) : undefined;
      const qs = new URLSearchParams();
      qs.set('purchaseOrderId', String(row.purchaseOrderId));
      if (row.purchaseOrderVersion != null) {
        qs.set('purchaseOrderVersion', String(row.purchaseOrderVersion));
      }
      if (parent) {
        qs.set('goods', parent);
      }
      qs.set('year', String(state.cropYear));
      qs.set('fromAddress', address.address);
      if (address.title) {
        qs.set('fromAddressTitle', address.title);
      }
      qs.set('prepayment', row.prepaymentType);
      if (typeof state.weightTon === 'number' && state.weightTon > 0) {
        qs.set('prefillWeightTon', String(state.weightTon));
      }
      // Оборудование продавца — по нему считалась цена ориентира. Передаём на экран встречной продажи,
      // чтобы серверный (авторитетный) пересчёт цены сделки учёл ровно те же параметры, что видел продавец.
      qs.set('hasScales', String(state.hasScales));
      if (state.hasScales && typeof state.scalesLength === 'number' && state.scalesLength > 0) {
        qs.set('scalesLength', String(state.scalesLength));
      }
      qs.set('trawls', String(state.trawls));
      qs.set('trailers', String(state.trailers));
      // Намеренный уход — снимаем защиту от несохранённого, иначе useBlocker перехватит навигацию.
      dirtyRef.current = false;
      navigate(`${ROUTES.APP_SELL_COUNTER}?${qs.toString()}`);
    },
    [
      address,
      state.goodsType,
      state.cropYear,
      state.weightTon,
      state.hasScales,
      state.scalesLength,
      state.trawls,
      state.trailers,
      parentGoodsTypes.data,
      navigate,
    ],
  );

  // Агент не публикует предложения о продаже (он посредник, а не сторона сделки) — на форму создания,
  // в т.ч. по прямому URL, его не пускаем: возвращаем к списку с пояснением «Агент не публикует предложения».
  if (isAgent) {
    return <Navigate to={ROUTES.APP_SELL} replace />;
  }

  // Ограничение по аттестации — создавать предложения нельзя без неё (прямой заход по URL тоже).
  // Аттестация — базовое требование, проверяем её раньше агентского договора.
  if (attestationRequired) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          <Anchor component={Link} to={ROUTES.APP_SELL} size="sm" c="brand.7">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {t('app.sell.back')}
            </Group>
          </Anchor>
          <SellAttestationGate />
        </Stack>
      </Container>
    );
  }

  // Ограничение договора — раздел недоступен для продажи без подписи (прямой заход по URL тоже).
  if (agencyMissed) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          <Anchor component={Link} to={ROUTES.APP_SELL} size="sm" c="brand.7">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {t('app.sell.back')}
            </Group>
          </Anchor>
          <AgencyGateBanner />
        </Stack>
      </Container>
    );
  }

  if (editMode && offerQuery.isError) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          <Anchor component={Link} to={ROUTES.APP_SELL} size="sm" c="brand.7">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {t('app.sell.back')}
            </Group>
          </Anchor>
          <Alert color="red" variant="light">
            {isServiceUnavailable(offerQuery.error) ? t('auth.errors.unavailable') : t('auth.errors.generic')}
          </Alert>
        </Stack>
      </Container>
    );
  }

  if (editMode && !prefillDone) {
    return (
      <Container size="md" py="xl">
        <Stack gap="md">
          <Skeleton h={28} w={240} />
          <Skeleton h={180} />
          <Skeleton h={180} />
        </Stack>
      </Container>
    );
  }

  const sectionTitle = (step: number, title: string): string => (editMode ? title : `${step} · ${title}`);

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Stack gap="xs">
          <Anchor component={Link} to={ROUTES.APP_SELL} size="sm" c="brand.7">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {t('app.sell.back')}
            </Group>
          </Anchor>
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon variant="light" color="brand" size={44} radius="md" style={{ flexShrink: 0 }}>
              <IconWheat size={26} />
            </ThemeIcon>
            <Stack gap={4}>
              <Title order={2}>{t(editMode ? 'app.sell.form.titleEdit' : 'app.sell.form.titleNew')}</Title>
              <Text size="sm" c="gray.7">
                {t('app.sell.form.immutableNote')}
              </Text>
            </Stack>
          </Group>
        </Stack>

        {/* 1 — Точка погрузки */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <SectionLabel>{sectionTitle(1, t('app.sell.form.sectionWhere'))}</SectionLabel>
            {editMode ? (
              <TextInput label={t('app.sell.form.address')} value={address?.title || address?.address || ''} disabled />
            ) : (
              <BasisPicker
                label={t('app.sell.form.address')}
                placeholder={t('app.sell.form.addressPlaceholder')}
                description={t('app.sell.form.addressHint')}
                ownBases={ownBases}
                value={address ? { address: address.address, addressTitle: address.title ?? '', basisId: address.basisId } : null}
                onChange={(picked) => {
                  dirtyRef.current = true;
                  setAddress(picked ? { address: picked.address, title: picked.addressTitle || undefined, basisId: picked.basisId } : null);
                  // Упреждающий прогрев кэша расстояний для ориентира: при выборе нового адреса — один раз
                  // на адрес, fire-and-forget. Это лишь ускоряет первый показ ориентира; корректность и
                  // непустоту гарантирует самолечение эндпоинта, даже если прогрев не успеет.
                  if (picked?.address && picked.address !== prewarmedAddressRef.current) {
                    prewarmedAddressRef.current = picked.address;
                    void bestPriceService.prewarmSellDistances(picked.address);
                  }
                }}
              />
            )}
          </Stack>
        </Card>

        {/* 2 — Культура и объём */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <SectionLabel>{sectionTitle(2, t('app.sell.form.sectionGoods'))}</SectionLabel>
            <Group grow align="flex-start" wrap="wrap">
              <Select
                label={t('app.sell.form.goods')}
                placeholder={t('app.sell.form.goodsPlaceholder')}
                // Биржевой оффер — только допустимая культура (НТБ): показываем и разрешаем лишь её.
                data={state.toExchange ? (goodsClasses.data ?? []).filter((g) => allowedGoods.includes(g)) : (goodsClasses.data ?? [])}
                value={state.goodsType}
                onChange={(value) => set({ goodsType: value, propsSetId: null })}
                searchable
                disabled={editMode || state.toExchange}
              />
              <Select
                label={mainPropertyName ?? t('app.sell.form.quality')}
                placeholder={state.goodsType ? t('app.sell.form.qualityPlaceholder') : t('app.sell.form.qualityFirst')}
                // Биржевой оффер — только допустимый набор качества (протеин 12,5%).
                data={state.toExchange ? propertyData.filter((o) => allowedSetId != null && o.value === String(allowedSetId)) : propertyData}
                value={state.propsSetId}
                onChange={(value) => set({ propsSetId: value })}
                disabled={editMode || !state.goodsType || propertySets.isLoading || state.toExchange}
              />
            </Group>
            <Group grow align="flex-start" wrap="wrap">
              <Select
                label={t('app.sell.form.year')}
                data={yearOptions}
                value={String(state.cropYear)}
                onChange={(value) => value && set({ cropYear: Number(value) })}
                disabled={editMode}
              />
              <NumberInput
                label={t('app.sell.form.weight')}
                value={state.weightTon}
                onChange={(value) => set({ weightTon: value === '' ? '' : Number(value) })}
                min={state.toExchange && exchangeConstraints ? exchangeConstraints.minTonnes : WEIGHT_MIN}
                max={state.toExchange && exchangeConstraints ? exchangeConstraints.maxTonnes : 999_999}
                step={state.toExchange && exchangeConstraints ? exchangeConstraints.lotSizeTonnes : undefined}
                allowNegative={false}
                decimalScale={0}
                clampBehavior="blur"
                error={
                  lotError
                    ? t(lotError, {
                        lot: exchangeConstraints?.lotSizeTonnes,
                        min: exchangeConstraints?.minTonnes,
                        max: exchangeConstraints?.maxTonnes,
                      })
                    : state.weightTon !== '' && state.weightTon < WEIGHT_MIN
                      ? t('app.sell.form.weightErr')
                      : undefined
                }
              />
            </Group>
            <Switch
              checked={state.toExchange}
              onChange={(e) => onToExchangeToggle(e.currentTarget.checked)}
              label={t('app.sell.form.toExchange')}
              description={t('app.sell.form.toExchangeHint')}
              disabled={exchangeToggleDisabled}
            />
            {state.toExchange && exchangeSession ? (
              <Text size="xs" c="gray.7">
                {t('app.sell.exchange.formPublishHint', {
                  start: exchangeSession.windowStart,
                  end: exchangeSession.windowEnd,
                })}
              </Text>
            ) : null}
          </Stack>
        </Card>

        {/* 3 — Цена и оплата */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <SectionLabel>{sectionTitle(3, t('app.sell.form.sectionPrice'))}</SectionLabel>
            {/* Ориентир «по какой цене можно продать»: внебиржевой — из прайсов покупателей; биржевой —
                лучшая нетто-FCA у ворот из стаканов (в правке, когда оффер уже существует), без CTA сделки. */}
            {!state.toExchange ? (
              <SellPriceOrientation
                address={address?.address}
                goodsType={state.goodsType}
                propsSetId={state.propsSetId}
                hasScales={state.hasScales}
                hasTrawl={state.trawls}
                hasTrailer={state.trailers}
                onSell={handleSellAtBest}
              />
            ) : editMode && exchangeOrientation ? (
              <ExchangeOrientationCard orientation={exchangeOrientation} />
            ) : null}
            <Group grow align="flex-start" wrap="wrap">
              <NumberInput
                label={t('app.sell.form.price')}
                value={state.price}
                onChange={(value) => set({ price: value === '' ? '' : Number(value) })}
                min={PRICE_MIN}
                max={999_999}
                allowNegative={false}
                decimalScale={2}
                thousandSeparator=" "
                clampBehavior="blur"
                error={state.price !== '' && state.price < PRICE_MIN ? t('app.sell.form.priceErr') : undefined}
              />
              <Select
                label={t('app.sell.form.prepayment')}
                // Биржевые офферы — всегда без предоплаты (зафиксировано, поле заблокировано).
                data={(prepaymentTypes.data ?? []).map((p) => ({ value: p.type, label: p.name }))}
                value={state.prepayment}
                onChange={(value) => set({ prepayment: value })}
                disabled={state.toExchange}
                description={state.toExchange ? t('app.sell.exchange.noPrepaymentHint') : undefined}
              />
            </Group>
            <Select
              label={t('app.sell.form.credit')}
              data={[
                { value: 'BY_AVERAGE_VALUE', label: t('app.sell.terms.creditAverage') },
                { value: 'BY_EACH_DAY', label: t('app.sell.terms.creditEachDay') },
              ]}
              value={state.qualityCreditType}
              onChange={(value) => set({ qualityCreditType: value })}
            />
            {state.price !== '' && state.weightTon !== '' ? (
              <Text size="sm" c="gray.8">
                {t('app.sell.form.total')}:{' '}
                <Text component="span" fw={700} c="brand.9">
                  {formatMoney(Number(state.price) * Number(state.weightTon))} ₽
                </Text>
                <NoNds />
              </Text>
            ) : null}
          </Stack>
        </Card>

        {/* 4 — Условия погрузки и хранения */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <SectionLabel>{sectionTitle(4, t('app.sell.form.sectionLogistics'))}</SectionLabel>
            <LoadingConditionsFields state={state} set={set} shipmentModes={shipmentModes.data ?? []} dateErrors={dateErrors} />
          </Stack>
        </Card>

        {/* 5 — Декларация */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <SectionLabel>{sectionTitle(5, t('app.sell.form.sectionDeclaration'))}</SectionLabel>
            <Group grow align="flex-start" wrap="wrap">
              <TextInput
                label={t('app.sell.form.declaration.no')}
                placeholder={t('app.sell.form.declaration.noPlaceholder')}
                value={state.declarationNo}
                maxLength={DECLARATION_NO_MAX}
                onChange={(e) => set({ declarationNo: e.currentTarget.value })}
              />
              <TextInput
                type="date"
                label={t('app.sell.form.declaration.date')}
                value={state.declarationDate}
                max={todayIso()}
                onChange={(e) => set({ declarationDate: e.currentTarget.value })}
              />
            </Group>
            <FileList files={state.declarationFiles} onRemove={(id) => removeDeclaration.mutate(id)} onDownload={(id) => downloadFile.mutate(id)} removing={removeDeclaration} />
            <UploadZone
              accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.gif"
              multiple
              loading={uploadDeclaration.isPending}
              hint={t('app.sell.form.declaration.filesHint')}
              onFiles={(files) => uploadDeclaration.mutate(files)}
            />
          </Stack>
        </Card>

        {/* 6 — Особые условия */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <SectionLabel>{sectionTitle(6, t('app.sell.form.sectionSpecial'))}</SectionLabel>
            <Switch
              checked={state.extraConditions}
              onChange={(e) => set({ extraConditions: e.currentTarget.checked })}
              label={t('app.sell.form.special.toggle')}
            />
            {state.extraConditions ? (
              <>
                <FileList files={state.specialTermsFiles} onRemove={(id) => removeSpecial.mutate(id)} onDownload={(id) => downloadFile.mutate(id)} removing={removeSpecial} />
                <UploadZone
                  accept=".pdf,.png,.jpg,.jpeg"
                  multiple
                  loading={uploadSpecial.isPending}
                  hint={t('app.sell.form.special.filesHint')}
                  onFiles={(files) => uploadSpecial.mutate(files)}
                />
              </>
            ) : null}
          </Stack>
        </Card>

        {/* 7 — Комментарий */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <SectionLabel>{sectionTitle(7, t('app.sell.form.sectionComment'))}</SectionLabel>
            <Textarea
              placeholder={t('app.sell.form.commentPlaceholder')}
              value={state.userComment}
              maxLength={COMMENT_MAX}
              autosize
              minRows={2}
              maxRows={6}
              onChange={(e) => set({ userComment: e.currentTarget.value })}
            />
          </Stack>
        </Card>

        {/* 8 — Поручение агенту */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <SectionLabel>{sectionTitle(8, t('app.sell.form.sectionAgent'))}</SectionLabel>
            <AgentAssignmentBlock
              draft={draft}
              signatureKey={signatureKey}
              fileId={agentFileId}
              signed={agentSigned}
              onChange={(fileId, signed) => {
                setAgentFileId(fileId);
                setAgentSigned(signed);
              }}
            />
          </Stack>
        </Card>

        {/* 9 — Согласия */}
        <Card withBorder padding="lg">
          <Stack gap="sm">
            <SectionLabel>{sectionTitle(9, t('app.sell.form.sectionAgree'))}</SectionLabel>
            <Checkbox
              checked={state.agreeSupply}
              onChange={(e) => set({ agreeSupply: e.currentTarget.checked })}
              label={t('app.sell.form.agree.supply')}
            />
            <Checkbox
              checked={state.agreeOffer}
              onChange={(e) => set({ agreeOffer: e.currentTarget.checked })}
              label={t('app.sell.form.agree.offer')}
            />
          </Stack>
        </Card>

        {submitError ? (
          <Alert color="red" variant="light">
            {submitError}
          </Alert>
        ) : null}

        {!canSubmit ? (
          <FormErrorsSummary title={t('common.fixFormErrors')} items={missing.map((key) => t(key))} />
        ) : null}

        <Group justify="flex-end">
          <Button
            size="md"
            disabled={!canSubmit || checking || submit.isPending}
            loading={checking || submit.isPending}
            onClick={handleSubmit}
          >
            {t(editMode ? 'app.sell.form.saveEdit' : 'app.sell.form.save')}
          </Button>
        </Group>
      </Stack>
    </Container>
  );
}

// Список загруженных файлов со скачать/удалить (крутилка только на удаляемом — урок из «Купить»).
